import React, { useState } from 'react';
import { ChevronDown } from 'lucide-react';

const Step = ({ n, text }) => (
  <div className="flex gap-3">
    <div className="w-6 h-6 rounded-full bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-400 text-xs font-bold flex items-center justify-center flex-shrink-0 mt-0.5">{n}</div>
    <p className="text-sm text-gray-700 dark:text-gray-300">{text}</p>
  </div>
);

const QA = ({ q, a }) => (
  <div>
    <p className="text-xs text-gray-400 dark:text-gray-500 mb-0.5">Q. {q}</p>
    <p className="text-sm text-gray-700 dark:text-gray-300">→ {a}</p>
  </div>
);

const sections = [
  {
    title: '체험 신청 처리',
    content: (
      <div className="space-y-3">
        <Step n={1} text="체험 신청 메뉴에서 새 신청을 확인하세요. 상단 알림 벨에 미처리 건수가 표시됩니다." />
        <Step n={2} text="신청자에게 전화 또는 이메일로 연락하세요." />
        <Step n={3} text="'체험 승인 + 초대링크 발급' 버튼을 누르면 초대링크가 자동 생성됩니다." />
        <Step n={4} text="복사 버튼으로 링크를 복사해 신청자에게 전달하세요. 대표자가 해당 링크로 가입합니다." />
        <Step n={5} text="가입 완료 후 '계약 성공' 또는 '계약 실패' 버튼으로 최종 처리합니다." />
        <QA q="초대링크를 다시 발급하려면?" a="'연락완료' 상태 카드에서 '초대링크 재발급' 버튼을 누르세요." />
        <QA q="계약 실패 시 사유를 기록하려면?" a="'계약 실패' 버튼 → 사유 입력 모달에서 내용 입력 후 저장하세요. 카드에 메모로 표시됩니다." />
        <QA q="신청 내역을 삭제하려면?" a="카드 우측 상단 휴지통 아이콘 → 확인 팝업에서 삭제하세요." />
      </div>
    ),
  },
  {
    title: '계정 관리',
    content: (
      <div className="space-y-3">
        <Step n={1} text="대표자가 초대링크로 가입하면 '승인 대기' 상태로 나타납니다." />
        <Step n={2} text="계정 관리에서 해당 계정의 '승인' 버튼을 누르세요." />
        <Step n={3} text="승인 후 대표자는 즉시 서비스를 이용할 수 있습니다." />
        <QA q="'매장 없음 (삭제됨)' 뱃지가 보여요" a="해당 대표자의 헬스장이 삭제된 상태입니다. 헬스장 관리에서 복구하거나, 신규 가입이 필요하면 계정 정지 후 재가입 안내를 하세요." />
        <QA q="계정을 정지하려면?" a="계정 관리 → 해당 계정 → '정지' 버튼" />
        <QA q="정지된 계정을 복구하려면?" a="계정 관리 → 정지 탭 → '복구' 버튼" />
        <QA q="초대코드를 직접 발급하려면?" a="계정 관리 하단 '초대 코드 발급' 버튼 → 코드를 대표자에게 전달 (7일 유효)" />
      </div>
    ),
  },
  {
    title: '헬스장 관리',
    content: (
      <div className="space-y-3">
        <QA q="헬스장 플랜을 변경하려면?" a="헬스장 관리 → 해당 헬스장 → '대신 관리' 또는 👁 상세보기 → 플랜 수정" />
        <QA q="플랜 종류는?" a="trial(무료체험), active(이용중), expired(만료), suspended(정지)" />
        <QA q="헬스장을 삭제하려면?" a="헬스장 관리 → 해당 헬스장 → 🗑 삭제 버튼 → 헬스장명 직접 입력 후 확인. 삭제 시 대표자 계정도 자동 정지됩니다." />
        <QA q="삭제된 헬스장을 복구하려면?" a="헬스장 관리 하단 '삭제됨' 섹션에서 복구 버튼을 누르세요. 삭제 후 7일 이내만 가능합니다." />
        <QA q="복구 후 플랜이 없어요" a="복구 시 계정은 정지 해제되지만 플랜은 별도로 설정해야 합니다. 헬스장 플랜을 trial 또는 active로 변경하세요." />
      </div>
    ),
  },
  {
    title: '알림 센터 사용법',
    content: (
      <div className="space-y-3">
        <p className="text-sm text-gray-700 dark:text-gray-300">우측 상단 🔔 벨 아이콘을 클릭하면 미처리 항목을 확인할 수 있습니다.</p>
        <QA q="알림 종류는?" a="미처리 체험 신청 (노란 점), 승인 대기 계정 (파란 점), 미처리 납부 요청 (녹색 점)" />
        <QA q="알림을 클릭하면?" a="해당 관리 페이지로 바로 이동합니다." />
        <QA q="알림이 갱신되지 않아요" a="알림은 60초마다 자동 갱신됩니다. 벨 아이콘을 클릭해 드롭다운을 열면 최신 상태가 반영됩니다." />
      </div>
    ),
  },
  {
    title: '납부 관리',
    content: (
      <div className="space-y-3">
        <Step n={1} text="대표자가 계좌이체 후 납부 요청을 등록하면 납부 관리 메뉴에 나타납니다." />
        <Step n={2} text="입금 확인 후 '확인' 버튼을 누르면 플랜이 자동 연장됩니다." />
        <Step n={3} text="입금이 확인되지 않으면 '거절' 버튼을 누르세요. 대표자에게 알림이 발송됩니다." />
        <QA q="납부 계좌는?" a="국민은행 703001-01-598295 (티앤티 주식회사)" />
        <QA q="요금제 금액은?" a="월 정기권 49,000원 / 연간 결제 490,000원 (2개월 무료)" />
      </div>
    ),
  },
];

export default function AdminHelp() {
  const [openSection, setOpenSection] = useState(0);

  return (
    <div className="max-w-2xl mx-auto space-y-3">
      {sections.map((section, idx) => {
        const isOpen = openSection === idx;
        return (
          <div
            key={idx}
            className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden"
          >
            <button
              onClick={() => setOpenSection(isOpen ? -1 : idx)}
              className="w-full flex items-center justify-between px-5 py-4 text-left"
            >
              <span className="font-semibold text-gray-900 dark:text-white text-sm">{section.title}</span>
              <ChevronDown
                className={`w-4 h-4 text-gray-400 transition-transform flex-shrink-0 ${isOpen ? 'rotate-180' : ''}`}
              />
            </button>
            {isOpen && (
              <div className="px-5 pb-5 border-t border-gray-100 dark:border-gray-700 pt-4">
                {section.content}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
